import React from "react";
import { useTranslation } from "react-i18next";

import { Box, InputAdornment, TextField } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { getEndOfPeriod } from "utils/debt";
import toIsoMidday from "utils/to-iso-midday";

import { FormValues } from "../../schemas";
import { ReferenceIndexSelect } from "../../form-fields/ReferenceIndexSelect";
import { FREQUENCY_OPTIONS } from "../constants";
import { IndexField } from "../types";
import { OptionCard } from "../OptionCard";
import { QuestionSlot } from "../QuestionSlot";

interface Step2Props {
  formik: FormikProps<FormValues>;
  dateFormat: string;
  isSimpleLoan: boolean;
  principalLinked: boolean | null;
  setPrincipalLinked: (v: boolean) => void;
  fetchIndexValue: (index: string, field: IndexField) => Promise<void>;
  reveal: (name: string) => void;
}

export const Step2PrincipalSetup: React.FC<Step2Props> = ({
  formik, dateFormat, isSimpleLoan,
  principalLinked, setPrincipalLinked, fetchIndexValue, reveal
}) => {
  const { t } = useTranslation("debt");
  const { values, setFieldValue } = formik;

  return (
    <Box>
      <QuestionSlot visible label={t("wizard.principalSetup.frequencyQuestion")}>
        <Select
          name="principal_frequency"
          label={t("wizard.principalSetup.frequencyLabel")}
          variant="outlined"
          fullWidth
          value={values.principal_frequency ?? ""}
          items={[{ key: "none", value: "", text: t("wizard.common.none") }, ...FREQUENCY_OPTIONS]}
          onChange={(e) => {
            const freq = e.target.value || null;
            setFieldValue("principal_frequency", freq);
            if (isSimpleLoan && freq && values.start_date)
              setFieldValue("principal_first_payment_date", getEndOfPeriod(dayjs(values.start_date), freq));
            reveal("principal_frequency");
          }}
        />
      </QuestionSlot>

      <QuestionSlot visible label={t("wizard.principalSetup.linkedQuestion")}>
        <OptionCard
          value="no"
          selected={principalLinked === false}
          onSelect={() => {
            setPrincipalLinked(false);
            setFieldValue("principal_reference_index", null);
            setFieldValue("principal_link_percent", 0);
          }}
          title={t("wizard.principalSetup.notLinked")}
          subtitle={t("wizard.principalSetup.notLinkedDesc")}
        />
        <OptionCard
          value="yes"
          selected={principalLinked === true}
          onSelect={() => setPrincipalLinked(true)}
          title={t("wizard.principalSetup.linked")}
          subtitle={t("wizard.principalSetup.linkedDesc")}
        />
      </QuestionSlot>

      <QuestionSlot visible={principalLinked === true} label={t("wizard.principalSetup.indexQuestion")}>
        <ReferenceIndexSelect
          name="principal_reference_index"
          formik={formik}
          label={t("wizard.principalSetup.indexLabel")}
          onIndexChange={(idx) => {
            fetchIndexValue(idx, "principal_index_value");
            reveal("principal_index_selected");
          }}
          onIndexClear={() => setFieldValue("principal_link_percent", 0)}
        />
      </QuestionSlot>

      <QuestionSlot
        visible={principalLinked === true && Boolean(values.principal_reference_index)}
        label={t("wizard.principalSetup.linkPercentQuestion")}
      >
        <TextField
          fullWidth
          type="number"
          label={t("wizard.common.indexLinkPercentage")}
          value={values.principal_link_percent || ""}
          onChange={(e) => setFieldValue("principal_link_percent", parseFloat(e.target.value) || 0)}
          slotProps={{ input: { endAdornment: <InputAdornment position="end">%</InputAdornment> } }}
        />
      </QuestionSlot>

      {isSimpleLoan && (
        <QuestionSlot visible label={t("wizard.principalSetup.firstPaymentQuestion")}>
          <DesktopDatePicker
            format={dateFormat}
            views={["year", "month", "day"]}
            label={t("wizard.principalSetup.firstPaymentLabel")}
            value={values.principal_first_payment_date ? dayjs(values.principal_first_payment_date) : null}
            onChange={(v) => setFieldValue("principal_first_payment_date", v?.isValid() ? toIsoMidday(v) : null)}
            sx={{ width: "100%" }}
            slotProps={{ textField: { helperText: t("wizard.common.autoCalculated") } }}
          />
        </QuestionSlot>
      )}
    </Box>
  );
};
